import * as Sentry from '@sentry/react'

import { ENV_SETTINGS } from '../global/envSettings'
import { getFeatures } from '../sharedLegacy/hooks/useFeatures'
import { request } from '../sharedLegacy/utils/api'
import { type APIResponse, HTTPMethod } from '../sharedLegacy/utils/api.types'
import { getRoomInfo } from '../global/blocs/roomInfo'

import { localStorageIntegration } from './integrations/localStorageIntegration'
import { pageLifecycleIntegration } from './integrations/pageLifecycleIntegration'

type TrackingInfoDTO = {
  type: 'target' | 'identity' | 'group'
  name: string
  value: string | number
}[]

const initLogging = () => {
  if (ENV_SETTINGS.environment === 'development') return
  if (build.SHA_SIGNATURE === 'local') return

  const roomInfo = getRoomInfo()

  // just in case, and it simplifies logic downstream
  if (roomInfo.role !== 'audience') return

  const client = Sentry.init({
    dsn: 'https://9e2c5a93d78d759bc23ecea836e44bfc@o198220.ingest.us.sentry.io/4508760594644992',
    release: build.SHA_SIGNATURE,
    environment: ENV_SETTINGS.environment,
    initialScope: {
      tags: {
        liveId: roomInfo.liveId,
        role: roomInfo.role,
      },
    },
    normalizeDepth: 10,
    integrations: [
      Sentry.captureConsoleIntegration(),
      Sentry.extraErrorDataIntegration({ depth: 10 }),
      Sentry.featureFlagsIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.reportingObserverIntegration(),
      pageLifecycleIntegration(),
      localStorageIntegration(),
    ],
  })

  void request<unknown, APIResponse<TrackingInfoDTO>>({
    url: '/api/2/user/tracking',
    method: HTTPMethod.GET,
  }).then((response) => {
    const { userId, email, ...user } = response.data.reduce<
      { userId?: number; email?: string } & Record<string, unknown>
    >((acc, { type, name, value }) => {
      if (['identity', 'group'].includes(type)) return { ...acc, [name]: value }
      return acc
    }, {})

    Sentry.setUser({ id: userId, email, display: userId })
    // @ts-expect-error — calculated violation
    Sentry.setTags(user)
  })

  const flagsIntegration =
    client?.getIntegrationByName<Sentry.FeatureFlagsIntegration>('FeatureFlags')
  if (flagsIntegration) {
    void getFeatures().then((features) => {
      Object.entries(features).forEach(([k, v]) => {
        flagsIntegration.addFeatureFlag(k, v)
      })
    })
  }
}

export { initLogging }
